import { useCallback, useEffect, useRef, useState } from 'react';
import { searchForms, type FormSummary } from '../api/catalog';
import { ApiError } from '../api/httpClientFactory';

export interface FormSearchState {
  status: 'loading' | 'ready' | 'error';
  items: FormSummary[];
  nextCursor: string | null;
  error: ApiError | null;
  loadingMore: boolean;
  moreError: ApiError | null;
  loadMore: () => void;
  retry: () => void;
}

function asApiError(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError('UNKNOWN', 0);
}

function isSessionExpired(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'SESSION_EXPIRED';
}

export function useFormSearch(q: string, categoryId: number | null): FormSearchState {
  const query = q.trim();
  const [status, setStatus] = useState<FormSearchState['status']>('loading');
  const [items, setItems] = useState<FormSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<ApiError | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const [seenQuery, setSeenQuery] = useState(query);
  const [seenCategory, setSeenCategory] = useState(categoryId);
  const [seenRetry, setSeenRetry] = useState(retryToken);

  if (seenQuery !== query || seenCategory !== categoryId || seenRetry !== retryToken) {
    setSeenQuery(query);
    setSeenCategory(categoryId);
    setSeenRetry(retryToken);
    setStatus('loading');
    setItems([]);
    setNextCursor(null);
    setError(null);
    setMoreError(null);
    setLoadingMore(false);
  }

  const generation = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const cursorRef = useRef<string | null>(null);
  const loadingMoreRef = useRef(false);
  const restartedRef = useRef(false);

  const retry = useCallback(() => {
    setRetryToken((token) => token + 1);
  }, []);

  useEffect(() => {
    const gen = generation.current + 1;
    generation.current = gen;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    cursorRef.current = null;
    loadingMoreRef.current = false;
    restartedRef.current = false;

    searchForms({ q: query, categoryId, cursor: null, signal: controller.signal }).then(
      (page) => {
        if (generation.current !== gen) return;
        setItems(page.items);
        setNextCursor(page.nextCursor);
        cursorRef.current = page.nextCursor;
        setStatus('ready');
      },
      (reason: unknown) => {
        if (generation.current !== gen) return;
        if (isSessionExpired(reason)) return;
        setError(asApiError(reason));
        setStatus('error');
      },
    );

    return () => controller.abort();
  }, [query, categoryId, retryToken]);

  const loadMore = useCallback(() => {
    const cursor = cursorRef.current;
    if (!cursor || loadingMoreRef.current) return;
    const gen = generation.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    setMoreError(null);
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    searchForms({ q: query, categoryId, cursor, signal: controller.signal }).then(
      (page) => {
        if (generation.current !== gen) return;
        loadingMoreRef.current = false;
        setLoadingMore(false);
        setItems((prev) => [...prev, ...page.items]);
        setNextCursor(page.nextCursor);
        cursorRef.current = page.nextCursor;
      },
      (reason: unknown) => {
        if (generation.current !== gen) return;
        loadingMoreRef.current = false;
        setLoadingMore(false);
        if (isSessionExpired(reason)) return;
        if (reason instanceof ApiError && reason.code === 'INVALID_CURSOR') {
          if (!restartedRef.current) {
            restartedRef.current = true;
            retry();
          }
          return;
        }
        setMoreError(asApiError(reason));
      },
    );
  }, [query, categoryId, retry]);

  // Discard an in-flight page after unmount. Declared after the fetch effect so its
  // cleanup runs first and the generation check ignores the abort.
  useEffect(() => {
    return () => {
      generation.current += 1;
      controllerRef.current?.abort();
    };
  }, []);

  return {
    status,
    items,
    nextCursor,
    error,
    loadingMore,
    moreError,
    loadMore,
    retry,
  };
}
